import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Controller, useForm, type FieldPath } from 'react-hook-form';
import { NJButton, NJFormItem, NJHeading, NJText } from '@engie-group/fluid-design-system-react';
import DataTable from '@shared/DataTable';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { UMSelect, UMSegmentedControl } from '../../urbanmap/_components';
import { PERIODS, USAGES, defaultRatios, ratioKey } from './data';
import { effectiveConsumption, fmt } from './domain';
import { DecisionNote } from './DecisionPanel';
import type { Building, Energy, Project } from './types';

interface RatioRow { usage: string; period: string; value: number }
export function ProjectSettings({ project, buildings, save, dirty, onDecision }: {
  project: Project; buildings: Building[]; save: (p: Project) => void;
  dirty: (value: boolean) => void; onDecision: (id: string) => void;
}) {
  const form = useForm<Project>({ defaultValues: structuredClone(project) });
  const [tab, setTab] = useState('Général');
  const [energy, setEnergy] = useState<Energy>('heat');
  const [cell, setCell] = useState(ratioKey(USAGES[0], PERIODS[0]));
  const [message, setMessage] = useState('');
  const draft = form.watch();
  const currentRatio = draft.ratios[energy][cell];
  useEffect(() => { dirty(form.formState.isDirty); }, [dirty, form.formState.isDirty]);
  const rows = useMemo(() => USAGES.flatMap(usage => PERIODS.map(period => ({
    usage, period, value: draft.ratios[energy][ratioKey(usage, period)],
  }))), [draft.ratios, energy, currentRatio]);
  const headers: ColDef<RatioRow>[] = [
    { field: 'usage', headerName: 'Usage' }, { field: 'period', headerName: 'Période' },
    { field: 'value', headerName: 'kWh/m²/an' },
    { headerName: 'Modifier', cellRenderer: (p: ICellRendererParams<RatioRow>) => p.data ? <NJButton label="Éditer"
      scale="sm" emphasis="minimal" onClick={() => { if (p.data) setCell(ratioKey(p.data.usage, p.data.period)); }} /> : null },
  ];
  const total = (p: Project) => buildings.reduce((sum, b) => sum + effectiveConsumption(b, p, energy), 0);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage('');
    form.clearErrors();
    const values = form.getValues();
    if (!values.name.trim()) {
      form.setError('name', { type: 'validate', message: 'Nom requis' });
      setTab('Général');
      return;
    }
    if (!Number.isFinite(values.dju) || values.dju <= 0) {
      form.setError('dju', { type: 'validate', message: 'DJU positif requis.' });
      setTab('DJU');
      return;
    }
    const invalidRatio = (['heat', 'cold'] as const).flatMap(ratioEnergy =>
      USAGES.flatMap(usage => PERIODS.map(period => ({
        energy: ratioEnergy, key: ratioKey(usage, period), value: values.ratios[ratioEnergy][ratioKey(usage, period)],
      })))).find(ratio => !Number.isFinite(ratio.value) || ratio.value <= 0);
    if (invalidRatio) {
      const field = `ratios.${invalidRatio.energy}.${invalidRatio.key}` as FieldPath<Project>;
      form.setError(field, { type: 'validate', message: 'Démonstration : ratio positif requis.' });
      setEnergy(invalidRatio.energy);
      setCell(invalidRatio.key);
      setTab('Ratios');
      return;
    }
    save(values);
    form.reset(values);
    setMessage('Paramètres enregistrés. Les plans calculés concernés sont signalés à recalculer.');
  };
  return <section className="sp-panel sp-stack">
    <NJHeading as="h2" scale="md">Paramètres du projet</NJHeading>
    <DecisionNote id="permissions" onOpen={onDecision} />
    <UMSegmentedControl options={['Général', 'Ratios', 'DJU']} value={tab} onChange={setTab} />
    <form className="sp-stack" onSubmit={submit}>
      {tab === 'Général' && <>
        <Controller name="name" control={form.control} rules={{ validate: v => !!v.trim() || 'Nom requis' }} render={({ field, fieldState }) =>
          <NJFormItem id="pu-project-name" label="Nom du projet" value={field.value} hasError={!!fieldState.error}
            onChange={e => { if (e.target instanceof HTMLInputElement) field.onChange(e.target.value); }} />} />
        <NJText>Périmètre : {project.perimeter} · copie du 16/10/2026.</NJText>
        <NJText>Nature : {project.networkKind === 'existing' ? 'réseau existant' : 'réseau nouveau'}. Aucun changement de périmètre simulé.</NJText>
      </>}
      {tab === 'Ratios' && <>
        <UMSelect ariaLabel="Énergie des ratios" value={energy} options={[{ value: 'heat', label: 'Chaleur' }, { value: 'cold', label: 'Froid' }]}
          onChange={v => { if (v === 'heat' || v === 'cold') setEnergy(v); }} />
        <div className="pu-ratio-grid"><DataTable data={rows} header={headers} testId="pu-ratios" isPaginated paginationPageSize={5} paginationPageSizeSelector={[5, 15, 30]} /></div>
        <Controller key={`${energy}-${cell}`} name={`ratios.${energy}.${cell}`} control={form.control}
          rules={{ validate: v => Number.isFinite(v) && v > 0 || 'Démonstration : ratio positif requis.' }}
          render={({ field, fieldState }) => <NJFormItem id="pu-ratio" label={`${cell} · kWh/m²/an`} type="number" step="any"
            value={Number.isFinite(field.value) ? String(field.value) : ''} hasError={!!fieldState.error} subscriptMessage={fieldState.error?.message}
            onChange={e => { if (e.target instanceof HTMLInputElement) field.onChange(e.target.valueAsNumber); }} />} />
        <NJButton label="Réinitialiser les ratios de cette énergie" emphasis="minimal" scale="sm"
          onClick={() => form.setValue(`ratios.${energy}`, defaultRatios(energy), { shouldDirty: true })} />
      </>}
      {tab === 'DJU' && <>
        <Controller name="station" control={form.control} render={({ field }) =>
          <UMSelect ariaLabel="Station météo" value={field.value} options={['Paris-Montsouris', 'Orly', 'Le Bourget'].map(v => ({ value: v, label: v }))}
            onChange={v => { field.onChange(v); form.setValue('stationDju', v === 'Orly' ? 2300 : v === 'Le Bourget' ? 2400 : 2200, { shouldDirty: true }); }} />} />
        <Controller name="dju" control={form.control} rules={{ validate: v => Number.isFinite(v) && v > 0 || 'DJU positif requis.' }}
          render={({ field, fieldState }) => <NJFormItem id="pu-dju" label="DJU de référence · base 18 °C" type="number"
            value={Number.isFinite(field.value) ? String(field.value) : ''} hasError={!!fieldState.error} subscriptMessage={fieldState.error?.message}
            onChange={e => { if (e.target instanceof HTMLInputElement) field.onChange(e.target.valueAsNumber); }} />} />
        <NJText>DJU station : {draft.stationDju}. Coefficient illustratif chaleur : {fmt(draft.stationDju / draft.dju)}.</NJText>
      </>}
      <div className="sp-notice sp-stack" role="status">
        <NJText as="div"><strong>Consommation globale du périmètre · {energy === 'heat' ? 'chaleur' : 'froid'}</strong></NJText>
        <NJText>Enregistrée : {fmt(total(project) / 1000)} GWh/an → aperçu : {Number.isFinite(total(draft)) ? fmt(total(draft) / 1000) : 'saisie invalide'} GWh/an.</NJText>
        <NJText scale="sm">Les corrections finales locales restent prioritaires et inchangées.</NJText>
        {form.formState.isDirty && <NJText>Modifications non enregistrées ; aucun plan recalculé.</NJText>}
      </div>
      <div className="sp-row">
        <NJButton label="Enregistrer les paramètres" type="submit" disabled={!form.formState.isDirty} />
        <NJButton label="Annuler les paramètres" emphasis="subtle" onClick={() => { form.reset(); setMessage(''); }} />
      </div>
    </form>
    {message && <NJText role="status">{message}</NJText>}
    <DecisionNote id="parameters" onOpen={onDecision} />
    <NJText scale="sm">Formule de démo : surface × ratio / 1 000 ; DJU station / référence pour la chaleur uniquement. Sans valeur d’ingénierie.</NJText>
  </section>;
}
